import Vue from "vue"
import screenService from "@/service/screen-service"
import mealService from "@/service/meal-service"
import offerService from "@/service/offer-service"
import schedulingService from "@/service/scheduling-service"
import authService from "@/service/auth-service"
import projectService from "@/service/project-service"
import memberService from "@/service/member-service"
import pairingService from "@/service/pairing-service"
import deviceService from "@/service/device-service"
import deviceStatusService from "@/service/device-status-service"
import cacheService from "@/service/cache-service"
import userService from "@/service/user-service"
import connectionService from "@/service/connection-service"
import { templates } from "@/components/screen"
import { throttle, offerScheduler } from "@/utils"
import { BastaError } from "@/errors"

// TODO: split into modules

const emptyProject = {
  id: undefined,
  name: undefined,
  members: {},
  devices: {},
  devicesStatus: {},
  data: {
    defaultImage: {
      imageUrl: "imgs/black.png"
    },
    meals: {},
    offers: {},
    scheduling: {},
    schedulingConf: {
      hours: {},
      days: {}
    },
    screens: undefined,
    restApi: undefined,
    currency: undefined,
    activeOfferIds: [],
    availableCategories: []
  }
}
const emptyUser = {
  projects: {}
}
const state = new Vue({
  data: {
    connection: false,
    pendingActionsSize: 0,
    errors: [],
    user: {
      ...emptyUser
    },
    project: {
      ...emptyProject
    },
    templates: templates
  },
  computed: {
    isDemoEnv() {
      return process.env.VUE_APP_FIREBASE_projectId === "chefcarte"
    },
    isUserLogged() {
      return !!this.user.email
    },
    isProjectInitialized() {
      return this.project.id != null
    },
    userRole() {
      if (this.isUserLogged) {
        const email = state.user.email
        return (
          state.project.members &&
          state.project.members[email] &&
          state.project.members[email].role
        )
      }
      return ""
    },
    isAdmin() {
      return this.userRole === "admin"
    },
    lang() {
      const userLang = this.user && this.user.lang
      const browserLang = navigator.language.substr(0, 2)
      return userLang || browserLang
    }
  },
  watch: {
    "project.data.scheduling"() {
      schedulingActions.schedule()
    },
    "project.data.schedulingConf"() {
      schedulingActions.schedule()
    }
  }
})

const messageActions = {
  addError: message => error => {
    Vue.$logger.error(message, error)
    state.errors.push(message)
  },
  removeError(index) {
    state.errors.splice(index, 1)
  },
  clearErrors() {
    if (state.errors.length > 0) {
      state.errors = []
    }
  },
  addDisappearingError(message) {
    if (state.errors.includes(message)) {
      return
    }
    state.errors.push(message)
    setTimeout(() => {
      const idx = state.errors.indexOf(message)
      if (idx > -1) {
        state.errors.splice(idx, 1)
      }
    }, 3000)
  }
}

const screenActions = {
  async addScreen() {
    const projectId = state.project.id
    const screens = await screenService.addOneScreen(projectId)
    state.project.data.screens = screens
  },
  removeScreen() {
    return Promise.all([
      offerService.removeScreenFromOfferConf(
        state.project.id,
        Object.keys(state.project.data.offers),
        state.project.data.screens - 1
      ),
      screenService.removeOneScreen(state.project.id)
    ]).then(([, res]) => {
      state.project.data.screens = res
    })
  },
  updateScreens(screens) {
    state.project.data.screens = screens
  }
}

const projectActions = {
  initProjectListeners(projectId, errorCallback) {
    const throttledErrorCallback = throttle(errorCallback)
    if (state.project.id !== projectId) {
      projectActions.terminateProjectListeners(state.project.id)
      state.project.id = projectId
      projectService.onProjectNameChanged(
        projectId,
        projectActions.updateProjectName,
        throttledErrorCallback
      )
      projectService.onProjectCurrencyChanged(
        projectId,
        cacheService.callAndCache(projectActions.updateProjectCurrency),
        throttledErrorCallback
      )
      projectService.onProjectDefaultImageChanged(
        projectId,
        cacheService.callAndCache(projectActions.updateProjectDefaultImage),
        throttledErrorCallback
      )
      offerService.onOffersChanged(
        projectId,
        cacheService.callAndCache(offerActions.updateOffers),
        throttledErrorCallback
      )
      schedulingService.onSchedulingChanged(
        projectId,
        cacheService.callAndCache(schedulingActions.updateScheduling),
        throttledErrorCallback
      )
      schedulingService.onSchedulingConfChanged(
        projectId,
        cacheService.callAndCache(schedulingActions.updateSchedulingConf),
        throttledErrorCallback
      ),
        screenService.onCountChanged(
          projectId,
          cacheService.callAndCache(screenActions.updateScreens),
          throttledErrorCallback
        )
      mealService.onMealsChanged(
        projectId,
        cacheService.callAndCache(mealActions.updateMeals),
        throttledErrorCallback
      )
      memberService.onMembersChanged(
        projectId,
        memberActions.updateMembers,
        throttledErrorCallback
      )
      deviceService.onDevicesChanged(
        projectId,
        cacheService.callAndCache(deviceActions.updateDevices),
        throttledErrorCallback
      )
      deviceStatusService.onDevicesStatusChanged(
        projectId,
        deviceStatusActions.updateDevicesStatus,
        throttledErrorCallback
      )
      projectService.onProjectRestApiChanged(
        projectId,
        projectActions.updateProjectRestApi,
        throttledErrorCallback
      )
      Vue.prototype.$ga.set("projectId", projectId)
    }
  },
  loadProjectDataFromCache() {
    const projectFromCache = cacheService.getProjectDataFromCache()
    state.project = { ...projectFromCache }
  },
  terminateProjectListeners(projectId) {
    Vue.prototype.$ga.set("projectId", "")
    state.project = { ...emptyProject }
    if (projectId != null) {
      projectService.offProjectNameChanged(projectId)
      projectService.offProjectCurrencyChanged(projectId)
      projectService.offProjectRestApiChanged(projectId)
      projectService.offProjectDefaultImageChanged(projectId)
      offerService.offOffersChanged(projectId)
      schedulingService.offSchedulingChanged(projectId)
      schedulingService.offSchedulingConfChanged(projectId)
      screenService.offCountChanged(projectId)
      mealService.offMealsChanged(projectId)
      memberService.offMembersChanged(projectId)
      deviceService.offDevicesChanged(projectId)
      deviceStatusService.offDevicesStatusChanged(projectId)
    }
  },
  async createProject(projectName, currency) {
    // TODO: use transaction?
    const project = await projectService.createProject(
      projectName,
      state.user.email,
      currency
    )
    await userService.setProjectToUser(state.user.email, project)
    state.user.projects[project.id] = projectName
    await schedulingActions.createSchedulingConf(project.id)
  },
  async createDemoProject(projectName, currency) {
    // TODO: use transaction?
    const project = await projectService.createProject(
      projectName,
      state.user.email,
      currency,
      true
    )
    await userService.setProjectToUser(state.user.email, project)
    state.user.projects[project.id] = projectName
  },
  async removeCurrentProject() {
    // TODO: use transaction?
    const projectId = state.project.id
    const members = state.project.members
    this.terminateProjectListeners(projectId)
    await Promise.all(
      Object.keys(members).map(email =>
        userService.removeProjectFromUser(email, projectId)
      )
    )
    return projectService.removeProject(projectId)
  },
  async setProjectName(projectName) {
    // TODO: use transaction?
    const projectId = state.project.id
    const members = state.project.members
    await projectService.setProjectName(projectId, projectName)
    state.project.name = projectName
    await Promise.all(
      Object.keys(members).map(email =>
        userService.setProjectToUser(email, state.project)
      )
    )
    state.user.projects[projectId] = projectName
  },
  async setProjectCurrency(currency) {
    // TODO: use transaction?
    const projectId = state.project.id
    await projectService.setProjectCurrency(projectId, currency)
    state.project.data.currency = currency
  },
  async setProjectRestApi(restApi) {
    // TODO: use transaction?
    const projectId = state.project.id
    await projectService.setProjectRestApi(projectId, restApi)
    state.project.data.restApi = restApi
  },
  async setProjectDefaultImage(projectId, defaultImage) {
    await projectService.setProjectDefaultImage(projectId, defaultImage)
    projectActions.updateProjectDefaultImage(defaultImage)
  },
  updateProjectName(projectName) {
    state.project.name = projectName
  },
  updateProjectCurrency(currency) {
    state.project.data.currency = currency
  },
  updateProjectRestApi(restApi) {
    state.project.data.restApi = restApi === true
  },
  updateProjectDefaultImage(defaultImage) {
    state.project.data.defaultImage = defaultImage || {
      imageUrl: "imgs/default-screen-image.png"
    }
  },
  getRestApiUrl() {
    return projectService.getRestApiUrl(state.project.id)
  }
}

const mealActions = {
  async createMeal(meal) {
    const projectId = state.project.id
    const mealCreated = await mealService.addMeal(projectId, meal)
    state.project.data.meals[mealCreated.id] = mealCreated
  },
  async updateMeal(mealId, meal) {
    const projectId = state.project.id
    await mealService.updateMeal(projectId, mealId, meal)
    state.project.data.meals[mealId] = meal
  },
  async removeMeal(mealId) {
    const projectId = state.project.id
    await mealService.removeMeal(projectId, mealId)
    delete state.project.data.meals[mealId]
    await Promise.all(
      Object.keys(state.project.data.offers).map(offerId =>
        offerActions.removeMealIdFromOffer(mealId, offerId)
      )
    )
  },
  updateMeals(meals) {
    state.project.data.meals = meals
    mealActions.updateMealCategories(meals)
  },
  updateMealCategories(meals) {
    const allCategories = Object.values(meals)
      .filter(m => m.categories)
      .map(m => m.categories)
      .reduce((cat1, cat2) => cat1.concat(cat2), [])
    const uniqueCategories = new Set(allCategories)
    const sortedCategories = Array.from(uniqueCategories).sort()
    state.project.data.availableCategories = sortedCategories
  }
}

const offerActions = {
  async addMealIdToOffer(mealId, offerId) {
    const projectId = state.project.id
    let offerMealIdsObj =
      (state.project.data.offers[offerId].data &&
        state.project.data.offers[offerId].data.mealIds) ||
      {}
    const offerMealIds = Object.values(offerMealIdsObj)
    const newRank = Math.max(...offerMealIds, 0) + 1
    offerMealIdsObj[mealId] = newRank
    offerMealIdsObj = offerActions.recreateOfferMealIdsRank(offerMealIdsObj)
    await offerService.saveMealIdsToOffer(projectId, offerMealIdsObj, offerId)
    offerActions.updateOfferMeals(offerId, offerMealIdsObj)
  },
  async removeMealIdFromOffer(mealId, offerId) {
    const projectId = state.project.id
    let offerMealIdsObj =
      (state.project.data.offers[offerId].data &&
        state.project.data.offers[offerId].data.mealIds) ||
      {}
    delete offerMealIdsObj[mealId]
    offerMealIdsObj = offerActions.recreateOfferMealIdsRank(offerMealIdsObj)
    await offerService.saveMealIdsToOffer(projectId, offerMealIdsObj, offerId)
    offerActions.updateOfferMeals(offerId, offerMealIdsObj)
  },
  async saveMealIdsToOffer(offerMeals, offerId) {
    const projectId = state.project.id
    const offerMealIds = offerMeals.reduce((acc, item, i) => {
      acc[item.id] = i + 1
      return acc
    }, {})
    await offerService.saveMealIdsToOffer(projectId, offerMealIds, offerId)
    offerActions.updateOfferMeals(offerId, offerMealIds)
  },
  getSortedOfferMeals(offerId) {
    const meals = state.project.data.meals
    const offerMealIds =
      offerId &&
      state.project.data.offers[offerId] &&
      state.project.data.offers[offerId].data &&
      state.project.data.offers[offerId].data.mealIds
    return offerService.sortOfferMealsByOrder(offerMealIds, meals)
  },
  updateOfferMeals(offerId, mealIds) {
    if (!state.project.data.offers[offerId].data) {
      return
    }
    state.project.data.offers[offerId].data.mealIds = mealIds
  },
  recreateOfferMealIdsRank(offerMealIds) {
    return Object.entries(offerMealIds)
      .sort((a, b) => a[1] - b[1])
      .reduce((acc, current, i) => {
        acc[current[0]] = i + 1
        return acc
      }, {})
  },
  generateOfferId() {
    const projectId = state.project.id
    return offerService.generateOfferId(projectId)
  },
  saveConfAndSchedulingToOffer(offerId, conf, scheduling) {
    const projectId = state.project.id
    return Promise.all([
      offerService.saveConfToOffer(projectId, offerId, conf),
      schedulingService.saveOfferScheduling(projectId, offerId, scheduling)
    ]).then(() => {
      state.project.data.scheduling[offerId] = scheduling
      state.project.data.offers[offerId].conf = conf
    })
  },
  deleteOffer(offerId) {
    return Promise.all([
      offerService.removeOffer(state.project.id, offerId),
      schedulingService.removeOfferScheduling(state.project.id, offerId, null)
    ]).then(() => {
      delete state.project.data.offers[offerId]
      delete state.project.data.scheduling[offerId]
    })
  },
  updateOffers(offers) {
    state.project.data.offers = offers
  }
}

const schedulingActions = {
  schedule() {
    offerScheduler.schedule(
      state.project.data.scheduling,
      state.project.data.schedulingConf,
      scheduledOfferIds => {
        state.project.data.activeOfferIds = scheduledOfferIds
      },
      e => {
        Vue.$logger.error("scheduling-offer-error", e)
      }
    )
  },
  async createSchedulingConf(projectId) {
    const conf = await schedulingService.createSchedulingConf(projectId)
    schedulingActions.updateSchedulingConf(conf)
  },
  saveSchedulingConfHour(hour) {
    const projectId = state.project.id
    return schedulingService.saveSchedulingConfHour(projectId, hour)
  },
  removeSchedulingConfHour(hourId) {
    const projectId = state.project.id
    // TODO: transaction
    return Promise.all([
      schedulingService.removeSchedulingConfHour(projectId, hourId),
      schedulingService.removeHourFromScheduling(
        projectId,
        state.project.data.scheduling,
        hourId
      )
    ])
  },
  updateScheduling(scheduling) {
    state.project.data.scheduling = scheduling
  },
  updateSchedulingConf(schedulingConf) {
    state.project.data.schedulingConf = schedulingConf
  }
}

const memberActions = {
  setRoleToMember(email, role) {
    const projectId = state.project.id
    return memberService.setRoleToMember(projectId, email, role)
  },
  async addMemberToProject(email, role) {
    if (state.project.members && state.project.members[email]) {
      throw new BastaError("member_already_exists")
    }
    await this.setRoleToMember(email, role)
    await userService.setProjectToUser(email, state.project)
  },
  async removeMemberFromProject(email) {
    const projectId = state.project.id
    await memberService.removeMember(projectId, email)
    await userService.removeProjectFromUser(email, projectId)
  },
  async setMealsSortByToMember(sortBy) {
    const email = state.user.email
    const projectId = state.project.id
    await memberService.setMealsSortByToMember(projectId, email, sortBy)
    state.project.members[email].mealsSortBy = sortBy
  },
  updateMembers(members) {
    state.project.members = members
  }
}

const pairingActions = {
  async addPairing(code) {
    const projectId = state.project.id
    if (await pairingService.pairingExists(code)) {
      throw new BastaError("pairing_code_exists")
    }
    return pairingService.addPairing(projectId, code)
  },
  waitForPairing(code) {
    return pairingService.onPairingMatchPromise(code)
  },
  async loginWithCredentials({ projectId, deviceId, code }) {
    const token = await pairingService.getToken(deviceId, code, projectId)
    deviceStatusService.onlineTracking(projectId, deviceId)
    return authService.loginWithCustomToken(token)
  },
  pairDevice(deviceId, code) {
    return pairingService.pairDevice(deviceId, code)
  }
}

const deviceActions = {
  async addScreenToDevice(deviceId, screenId) {
    await deviceService.addScreenIdToDevice(
      state.project.id,
      deviceId,
      screenId
    )
    this.updateScreenForDevice(deviceId, screenId)
  },
  async removeScreenFromDevice(deviceId) {
    await deviceService.removeScreenIdFromDevice(state.project.id, deviceId)
    this.updateScreenForDevice(deviceId)
  },
  removeScreenFromAllDevices(screenId) {
    const toRemoveScreen = Object.values(state.project.devices)
      .filter(device => device.screenId === screenId)
      .map(device => this.removeScreenFromDevice(device.id))
    return Promise.all(toRemoveScreen)
  },
  async removeDeviceFromProject(deviceId) {
    await deviceService.removeDeviceFromProject(state.project.id, deviceId)
    delete state.project.devices[deviceId]
  },
  async setDeviceName(deviceId, deviceName) {
    await deviceService.setDeviceName(state.project.id, deviceId, deviceName)
    state.project.devices[deviceId].name = deviceName
  },
  updateDevices(devices) {
    state.project.devices = devices
  },
  updateScreenForDevice(deviceId, screenId) {
    state.project.devices[deviceId] = {
      ...state.project.devices[deviceId],
      screenId
    }
  }
}

const deviceStatusActions = {
  updateDevicesStatus(devicesStatus) {
    state.project.devicesStatus = devicesStatus
  }
}

const authActions = {
  initAuthListener(signInCallback, signOutCallback, errorCallback) {
    this.terminateAuthListener()
    authService.onAuthChanged(auth => {
      if (auth == null || auth.email == null) {
        signOutCallback()
      } else {
        userActions.initUserListener(auth.email, signInCallback, errorCallback)
      }
    }, errorCallback)
  },
  terminateAuthListener() {
    authService.offAuthChanged()
  },
  loginWithEmail(email, password) {
    return authService.loginWithEmail(email, password)
  },
  async loginWithGoogle() {
    const userCredentials = await authService.loginWithGoogle()
    return userService.createUser(userCredentials)
  },
  async signUpWithEmail(email, password) {
    const userCredentials = await authService.signUpWithEmail(email, password)
    return userService.createUser(userCredentials)
  },
  sendResetPasswordEmail(email) {
    return authService.sendPasswordResetEmail(email)
  },
  logout() {
    projectActions.terminateProjectListeners(state.project.id)
    userActions.terminateUserListener()
    authActions.terminateAuthListener()
    authService.logout()
  }
}

const userActions = {
  initUserListener(email, nextCallback, errorCallback) {
    if (state.user.email === email) {
      nextCallback(state.user)
    } else {
      this.terminateUserListener(state.user.email)
      userService.onUserChanged(
        email,
        user => {
          state.user = user
          nextCallback(user)
          Vue.prototype.$ga.set("userId", email)
        },
        errorCallback
      )
    }
  },
  terminateUserListener() {
    const email = state.user.email
    state.user = { ...emptyUser }
    if (email != null) {
      userService.offUserChanged(email)
    }
    Vue.prototype.$ga.set("userId", "")
  },
  setLangToUser(email, lang) {
    return userService.setLangToUser(email, lang)
  }
}

const connectionActions = {
  initConnectionListener() {
    connectionService.onConnectionChanged(
      connectionActions.updateConnectionState
    )
  },
  updateConnectionState(conState) {
    state.connection = conState
  }
}

function interceptActions(actionObj, before, after) {
  return Object.keys(actionObj).reduce((acc, actionName) => {
    acc[actionName] = function(...args) {
      const actionFn = actionObj[actionName]
      const result = actionFn.apply(this, args)
      if (!result || !result.then) {
        //is not promise
        return result
      }
      before()
      return result
        .then(result => {
          after()
          return result
        })
        .catch(err => {
          after()
          throw err
        })
    }
    return acc
  }, {})
}

function actionWrap(actionObj) {
  return interceptActions(
    actionObj,
    function before() {
      state.pendingActionsSize++
    },
    function after() {
      state.pendingActionsSize--
    }
  )
}

const actions = {
  ...actionWrap(screenActions),
  ...actionWrap(mealActions),
  ...actionWrap(offerActions),
  ...actionWrap(authActions),
  ...actionWrap(projectActions),
  ...actionWrap(memberActions),
  ...actionWrap(pairingActions),
  ...actionWrap(deviceActions),
  ...actionWrap(userActions),
  ...actionWrap(schedulingActions),
  ...connectionActions,
  ...messageActions,
  ...deviceStatusActions
}

export { state, actions }
